import Image from 'next/image'

import { type PortableTextBlock, stegaClean } from 'next-sanity'

import { buttonSizeClasses } from '@/app/components/Button'
import { ImageWithDecor } from '@/app/components/ImageWithDecor'
import PortableText from '@/app/components/PortableText'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import type { SideBySide as SideBySideProps } from '@/sanity.types'
import { urlForImage } from '@/sanity/lib/utils'

type SideBySideComponentProps = {
  block: SideBySideProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
}

export default function SideBySide({ block, index, pageId, pageType }: SideBySideComponentProps) {
  if (!block?.items?.length) return null

  const isLCP = index < 2

  return (
    <div className="content-container my-7xl lg:my-11xl">
      <div className="mx-auto flex max-w-[1010px] flex-col gap-5xl md:gap-7xl lg:gap-8xl">
        {block.items.map((item, idx) => {
          if (!item?.content || !item?.image?.image) return null

          const imagePosition = stegaClean(item.imagePosition) || 'right'
          const isImageRight = imagePosition === 'right'
          const decor = stegaClean(item.image?.decor) || 'none'
          // Prioritize first image in first section for LCP optimization
          const isPriorityImage = isLCP && idx === 0

          return (
            <div
              key={item._key || idx}
              className={cn(
                'flex flex-col gap-4xl',
                'md:flex-row md:items-center md:gap-6xl lg:gap-14',
                isImageRight && 'md:flex-row-reverse',
              )}
            >
              <div className="flex-1">
                {item.image?.image?.asset &&
                  (() => {
                    const baseUrl = urlForImage(item.image.image)?.url()

                    if (!baseUrl) return null

                    return (
                      <ImageWithDecor decor={decor}>
                        <div className="relative aspect-[21/16] w-full overflow-hidden rounded-md lg:rounded-lg">
                          <Image
                            loader={({ width }) => {
                              return (
                                urlForImage(item.image.image)
                                  ?.width(width)
                                  .height(Math.round((width * 16) / 21))
                                  .url() || baseUrl
                              )
                            }}
                            src={baseUrl}
                            alt={item.image.image.alt || ''}
                            fill
                            className="object-cover"
                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 420px"
                            {...(isPriorityImage
                              ? { priority: true, fetchPriority: 'high' as const }
                              : { loading: 'lazy' as const })}
                          />
                        </div>
                      </ImageWithDecor>
                    )
                  })()}
              </div>
              <div className="flex-1">
                <PortableText
                  tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                  buttonClassName={buttonSizeClasses({ lg: 'md' })}
                  paragraphClassName="size-body-sm lg:size-body-lg"
                  value={item.content as PortableTextBlock[]}
                  documentId={pageId}
                  documentType={pageType}
                  parentPath={
                    block._key && item._key
                      ? `pageBuilder[_key=="${block._key}"].items[_key=="${item._key}"].content`
                      : undefined
                  }
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
